import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Paper,
  Box,
  CircularProgress,
  Breadcrumbs,
  Link as MuiLink,
  Alert,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Link } from "react-router-dom";
import * as XLSX from "xlsx";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";

const TableViewPage = ({ excelFile, title, headerRow = 1, skipRows = 0 }) => {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadExcelFile = async () => {
      try {
        const response = await fetch(excelFile);
        const arrayBuffer = await response.arrayBuffer();
        const workbook = XLSX.read(arrayBuffer, { type: "array" });
        
        // Вземи първия лист
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        
        // Конвертирай в JSON с опция defval за празни клетки
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { 
          header: 1, 
          defval: "" 
        });
        
        if (jsonData.length <= headerRow) {
          setError("Файлът няма достатъчно редове");
          setLoading(false);
          return;
        }
        
        // Вземи header row (по подразбиране ред 1 - индекс 1)
        const headers = jsonData[headerRow];
        
        // Филтрирай празни header-и
        const validHeaders = headers.map((header, index) => ({
          header: header || `Колона ${index + 1}`,
          index: index
        })).filter(h => h.header.toString().trim() !== "");
        
        // Създай колони за DataGrid
        const cols = validHeaders.map((h, colIndex) => {
          const headerLength = h.header.toString().length;
          const calculatedWidth = Math.max(120, Math.min(300, headerLength * 8));
          
          return {
            field: `col${h.index}`,
            headerName: h.header.toString(),
            width: calculatedWidth,
            sortable: true,
            filterable: true,
            headerAlign: 'center',
            align: colIndex === 0 ? 'left' : 'center',
          };
        });
        
        // Създай редове за DataGrid (skip header rows + additional rows)
        const dataRows = jsonData
          .slice(headerRow + 1 + skipRows)
          .filter(row => row.some(cell => cell !== "")) // Премахни празни редове
          .map((row, rowIndex) => {
            const rowData = { id: rowIndex };
            validHeaders.forEach((h) => {
              rowData[`col${h.index}`] = row[h.index] || "";
            });
            return rowData;
          });
        
        setColumns(cols);
        setRows(dataRows);
        setLoading(false);
      } catch (error) {
        console.error("Грешка при зареждане на файла:", error);
        setError(`Грешка при зареждане: ${error.message}`);
        setLoading(false);
      }
    };

    loadExcelFile();
  }, [excelFile, headerRow, skipRows]);

  // Българска локализация за DataGrid
  const bgBGLocale = {
    toolbarDensity: 'Плътност',
    toolbarDensityLabel: 'Плътност',
    toolbarDensityCompact: 'Компактна',
    toolbarDensityStandard: 'Стандартна',
    toolbarDensityComfortable: 'Комфортна',
    toolbarColumns: 'Колони',
    toolbarColumnsLabel: 'Избери колони',
    toolbarFilters: 'Филтри',
    toolbarFiltersLabel: 'Покажи филтрите',
    toolbarFiltersTooltipHide: 'Скрий филтрите',
    toolbarFiltersTooltipShow: 'Покажи филтрите',
    toolbarExport: 'Експорт',
    toolbarExportLabel: 'Експорт',
    toolbarExportCSV: 'Изтегли като CSV',
    toolbarExportPrint: 'Принтирай',
    
    columnsPanelTextFieldLabel: 'Намери колона',
    columnsPanelTextFieldPlaceholder: 'Заглавие на колона',
    columnsPanelShowAllButton: 'Покажи всички',
    columnsPanelHideAllButton: 'Скрий всички',
    
    filterPanelAddFilter: 'Добави филтър',
    filterPanelDeleteIconLabel: 'Изтрий',
    filterPanelOperators: 'Оператори',
    filterPanelOperatorAnd: 'И',
    filterPanelOperatorOr: 'Или',
    filterPanelColumns: 'Колони',
    filterPanelInputLabel: 'Стойност',
    filterPanelInputPlaceholder: 'Стойност на филтъра',
    
    filterOperatorContains: 'съдържа',
    filterOperatorEquals: 'равно на',
    filterOperatorStartsWith: 'започва с',
    filterOperatorEndsWith: 'завършва с',
    filterOperatorIs: 'е',
    filterOperatorNot: 'не е',
    filterOperatorAfter: 'е след',
    filterOperatorOnOrAfter: 'е на или след',
    filterOperatorBefore: 'е преди',
    filterOperatorOnOrBefore: 'е на или преди',
    filterOperatorIsEmpty: 'е празно',
    filterOperatorIsNotEmpty: 'не е празно',
    filterOperatorIsAnyOf: 'е едно от',
    
    columnMenuLabel: 'Меню',
    columnMenuShowColumns: 'Покажи колони',
    columnMenuFilter: 'Филтър',
    columnMenuHideColumn: 'Скрий',
    columnMenuUnsort: 'Премахни сортиране',
    columnMenuSortAsc: 'Сортирай възходящо',
    columnMenuSortDesc: 'Сортирай низходящо',
    
    columnHeaderFiltersTooltipActive: (count) =>
      count !== 1 ? `${count} активни филтъра` : `${count} активен филтър`,
    columnHeaderFiltersLabel: 'Покажи филтрите',
    columnHeaderSortIconLabel: 'Сортирай',
    
    noRowsLabel: 'Няма редове',
    noResultsOverlayLabel: 'Няма намерени резултати.',
    
    footerRowSelected: (count) =>
      count !== 1
        ? `${count.toLocaleString()} избрани редове`
        : `${count.toLocaleString()} избран ред`,
    footerTotalRows: 'Общо редове:',
    footerTotalVisibleRows: (visibleCount, totalCount) =>
      `${visibleCount.toLocaleString()} от ${totalCount.toLocaleString()}`,
    
    MuiTablePagination: {
      labelRowsPerPage: 'Редове на страница:',
      labelDisplayedRows: ({ from, to, count }) =>
        `${from}–${to} от ${count !== -1 ? count : `повече от ${to}`}`,
    },
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4, textAlign: "center" }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Зареждане на данни...</Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        sx={{ mb: 3 }}
      >
        <MuiLink component={Link} to="/database" underline="hover" color="inherit">
          База данни
        </MuiLink>
        <Typography color="text.primary">{title}</Typography>
      </Breadcrumbs>

      <Paper elevation={2} sx={{ p: { xs: 2, md: 3 } }}>
        <Typography 
          variant="h4" 
          gutterBottom 
          sx={{ 
            fontWeight: 600, 
            mb: 3,
            fontSize: { xs: '1.5rem', md: '2rem' }
          }}
        >
          {title}
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Общо {rows.length} реда
        </Typography>

        <Box sx={{ 
          height: { xs: 500, md: 650 }, 
          width: "100%",
          "& .MuiDataGrid-root": {
            fontSize: { xs: '0.75rem', md: '0.875rem' },
            border: '2px solid #e0e0e0',
            borderRadius: 2,
          }
        }}>
          <DataGrid
            rows={rows}
            columns={columns}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 25 },
              },
            }}
            pageSizeOptions={[10, 25, 50, 100]}
            disableRowSelectionOnClick
            localeText={bgBGLocale}
            sx={{
              "& .MuiDataGrid-cell": {
                borderRight: "1px solid #e0e0e0",
                py: 1.5,
                px: 1,
              },
              "& .MuiDataGrid-columnHeaders": {
                background: "#ff8c42 !important",
                color: "#ffffff !important",
                fontSize: { xs: '0.75rem', md: '0.875rem' },
                minHeight: "100px !important",
                maxHeight: "160px !important",
              },
              "& .MuiDataGrid-columnHeader": {
                py: 2,
                px: 1,
                backgroundColor: "transparent !important",
                minHeight: "100px !important",
                "&:hover": {
                  backgroundColor: "transparent !important",
                  "& .MuiIconButton-root": {
                    backgroundColor: "transparent !important",
                  }
                },
                "& .MuiIconButton-root": {
                  backgroundColor: "transparent !important",
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.15) !important",
                  }
                }
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                whiteSpace: "normal",
                lineHeight: "1.4",
                fontWeight: 700,
                textAlign: "center",
                color: "#ffffff !important",
                wordBreak: "keep-all",
                overflow: "visible",
                padding: "8px 4px",
              },
              "& .MuiDataGrid-columnSeparator": {
                color: "rgba(255, 255, 255, 0.3)",
              },
              "& .MuiDataGrid-sortIcon": {
                color: "#ffffff !important",
                opacity: 1,
              },
              "& .MuiDataGrid-menuIconButton": {
                color: "#ffffff !important",
                opacity: 1,
                backgroundColor: "transparent !important",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.15) !important",
                },
                "&.Mui-disabled": {
                  color: "rgba(255, 255, 255, 0.5) !important",
                }
              },
              "& .MuiDataGrid-iconButtonContainer": {
                "& .MuiIconButton-root": {
                  color: "#ffffff !important",
                  opacity: 1,
                  backgroundColor: "transparent !important",
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.15) !important",
                  }
                }
              },
              "& .MuiDataGrid-filterIcon": {
                color: "#ffffff !important",
                opacity: 1,
              },
              "& .MuiDataGrid-row": {
                "&:nth-of-type(odd)": {
                  backgroundColor: "#f9fafb",
                },
                "&:hover": {
                  backgroundColor: "#f0f4ff",
                },
              },
              "& .MuiDataGrid-footerContainer": {
                borderTop: "2px solid #e0e0e0",
                backgroundColor: "#f9fafb",
              },
            }}
          />
        </Box>
      </Paper>
    </Container>
  );
};

export default TableViewPage;